import React, { useState } from 'react';
import { IconSparkle, IconSend } from './Icons';
import './AskAimy.css';

// Direction B persistent command bar — a floating pill fixed near the bottom of
// the home screen. Hero mode (near-black) until the workspace is caught up,
// then ambient (white). Opens the ask sheet.
export function CommandBar({ hero = true, onOpen }) {
  return (
    <div className="command-bar-wrap">
      <button type="button" className={`command-bar ${hero ? 'hero' : 'ambient'}`} onClick={onOpen}>
        <span className="command-bar-mark" aria-hidden="true">
          <IconSparkle size={17} />
        </span>
        <span className="command-bar-label">Ask Aimy anything…</span>
      </button>
    </div>
  );
}

const SUGGESTIONS = ['What did I miss?', 'What’s blocked?', 'What decisions changed this week?'];

// Direction B ask sheet — a bottom sheet over a scrim. Idle shows suggestion
// chips; asking shows a thinking line then Aimy's answer + action pills that
// close the sheet and navigate / mark caught up. `onAsk` returns a promise
// resolving to the answer text.
export function AskSheet({ onClose, onAsk, onSeeDecisions, onSeeTodos, onMarkCaughtUp }) {
  const [query, setQuery] = useState('');
  const [thinking, setThinking] = useState(false);
  const [answer, setAnswer] = useState('');

  const ask = async (q) => {
    const text = (q ?? query).trim();
    if (!text || thinking) return;
    setQuery(text);
    setThinking(true);
    setAnswer('');
    try {
      const resp = await onAsk(text);
      setAnswer(resp || 'I couldn’t find an answer for that.');
    } catch {
      setAnswer('Something went wrong — please try again.');
    } finally {
      setThinking(false);
    }
  };

  return (
    <div className="ask-sheet-root" role="dialog" aria-modal="true" aria-label="Ask Aimy">
      <div className="ask-sheet-scrim" onClick={onClose} />
      <div className="ask-sheet">
        <div className="ask-sheet-grabber" />
        <div className="ask-input-pill">
          <span className="ask-input-mark" aria-hidden="true">
            <IconSparkle size={16} />
          </span>
          <input
            className="ask-input"
            value={query}
            autoFocus
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && ask()}
            placeholder="Ask Aimy anything…"
            aria-label="Ask Aimy anything"
          />
          <button
            type="button"
            className={`ask-send ${query.trim() ? 'active' : ''}`}
            onClick={() => ask()}
            disabled={!query.trim() || thinking}
            aria-label="Send"
          >
            <IconSend size={16} />
          </button>
        </div>

        {!answer && !thinking && (
          <div className="ask-suggestions">
            {SUGGESTIONS.map((s) => (
              <button key={s} type="button" className="ask-chip" onClick={() => ask(s)}>
                {s}
              </button>
            ))}
          </div>
        )}

        {thinking && (
          <div className="ask-thinking">
            <IconSparkle size={14} /> Aimy is thinking…
          </div>
        )}

        {answer && !thinking && (
          <div className="ask-answer">
            <p className="ask-answer-text">{answer}</p>
            <div className="ask-answer-actions">
              {onSeeDecisions && (
                <button
                  type="button"
                  className="ask-action decisions"
                  onClick={() => {
                    onClose();
                    onSeeDecisions();
                  }}
                >
                  See decisions
                </button>
              )}
              {onSeeTodos && (
                <button
                  type="button"
                  className="ask-action todos"
                  onClick={() => {
                    onClose();
                    onSeeTodos();
                  }}
                >
                  See todos
                </button>
              )}
              {onMarkCaughtUp && (
                <button
                  type="button"
                  className="ask-action ghost"
                  onClick={() => {
                    onMarkCaughtUp();
                    onClose();
                  }}
                >
                  Mark caught up
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
